import { formatMass, mass, type Gym, type Unit } from '@repwise/domain';

/** Bar, collars and plates-per-side, used in onboarding (J1 step 7) and Settings → Gym (J6). */
export function GymEditor({ gym, unit, onChange }: { gym: Gym; unit: Unit; onChange: (g: Gym) => void }) {
  const u = unit;
  const step = (field: 'bar' | 'collar', d: number) => onChange({ ...gym, [field]: Math.max(0, gym[field] + mass(d)) });
  const setCount = (w: number, d: number) => onChange({ ...gym, plates: gym.plates.map((p) => (p.weight === w ? { ...p, perSide: Math.max(0, Math.min(20, p.perSide + d)) } : p)) });
  const sorted = [...gym.plates].sort((a, b) => b.weight - a.weight);
  const Row = ({ label, value, onMinus, onPlus, hint }: { label: string; value: string; onMinus: () => void; onPlus: () => void; hint?: string }) => (
    <li className="flex items-center justify-between bg-surface-raised rounded-[14px] p-3">
      <span>
        {label}
        {hint && <span className="block text-xs text-text-muted">{hint}</span>}
      </span>
      <span className="flex items-center gap-3">
        <button type="button" className="min-w-11 min-h-11 rounded-full border border-outline" onClick={onMinus} aria-label={`Less ${label}`}>
          −
        </button>
        <span className="num text-headline w-20 text-center">{value}</span>
        <button type="button" className="min-w-11 min-h-11 rounded-full border border-outline" onClick={onPlus} aria-label={`More ${label}`}>
          +
        </button>
      </span>
    </li>
  );
  const big = u === 'kg' ? 2.5 : 5;
  return (
    <div className="space-y-4">
      <ul className="space-y-2">
        <Row label="Bar" value={`${formatMass(gym.bar)} ${u}`} onMinus={() => step('bar', -big)} onPlus={() => step('bar', big)} />
        <Row label="Each collar" value={`${formatMass(gym.collar)} ${u}`} hint="Leave at 0 if you don't use collars" onMinus={() => step('collar', u === 'kg' ? -0.5 : -1)} onPlus={() => step('collar', u === 'kg' ? 0.5 : 1)} />
      </ul>
      <div>
        <h3 className="text-headline">Plates you have, per side</h3>
        <p className="text-sm text-text-muted">Targets are always rounded to weights you can actually load.</p>
        <ul className="space-y-2 mt-2">
          {sorted.map((p) => (
            <Row key={p.weight} label={`${formatMass(p.weight)} ${u}`} value={`× ${p.perSide}`} onMinus={() => setCount(p.weight, -1)} onPlus={() => setCount(p.weight, 1)} />
          ))}
        </ul>
        {!gym.plates.some((p) => p.weight < mass(u === 'kg' ? 2.5 : 5) && p.perSide > 0) && (
          <button
            type="button"
            className="mt-2 text-now-ink underline min-h-11"
            onClick={() => onChange({ ...gym, plates: [...gym.plates.filter((p) => p.weight !== mass(u === 'kg' ? 1.25 : 2.5)), { weight: mass(u === 'kg' ? 1.25 : 2.5), perSide: 2 }] })}
          >
            I have small plates ({u === 'kg' ? '1.25 kg' : '2.5 lb'})
          </button>
        )}
      </div>
    </div>
  );
}
